import { Suspense, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useContactActions } from "@/app/Contacts/Hooks/useContactActions";
import { Badge } from "@/components/catalyst/badge";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { updateContactMutation } from "@/app/Contacts/GraphQL/contactMutations";
import {
  UpdateContactInput,
  ModelType,
  ContactType,
  PaymentStatus,
} from "@/gql/graphql";
import { useContactDrawer } from "./useContactDrawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { ContactDrawerTabs } from "./ContactDrawerTabs";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import { ContactDetailsForm } from "@/app/Contacts/Forms/ContactDetailsForm";
import { AddressInput } from "@/app/Address/Components/AddressInput";
import { contactTypeColorMap } from "@/app/Contacts/Utils/contactTypeColorMap";
import { useTranslations } from "use-intl";
import { ContactEntityTable } from "@/app/Contacts/Components/ContactEntityTable";
import { ContactEntityCreateDialog } from "@/app/Contacts/Components/ContactEntityCreateDialog";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Button } from "@/components/catalyst/button";
import { PlusIcon } from "@heroicons/react/24/outline";
import { InvoiceTable } from "@/app/Invoices/Components/InvoiceTable";
import { PaymentTable } from "@/app/Payments/Components/PaymentTable";
import { ContactCreditTable } from "@/app/ContactCredits/Components/ContactCreditTable";
import { ContactCreditBalances } from "@/app/ContactCredits/Components/ContactCreditBalances";
import { IssueContactCreditButton } from "@/app/ContactCredits/Components/IssueContactCreditButton";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

export function ContactDrawer() {
  const navigate = useNavigate();
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}",
  });
  const [activeTab, setActiveTab] = useState("details");

  const isOpen = !!search.contactId;

  function handleClose() {
    navigate({
      to: ".",
      search: (prev: any) => {
        return {
          ...prev,
          contactId: undefined,
        };
      },
    });
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} className="relative">
      <DrawerOverlay />

      <DrawerContent showCloseButton={true} onClose={handleClose}>
        {search.contactId ? (
          <div className="flex h-full flex-col overflow-hidden">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <DialogHeading
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />

              <DrawerBody>
                <TabContent activeTab={activeTab} />
              </DrawerBody>
            </Suspense>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

function TabContent({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    case "details":
      return <ContactDrawerDetailsScreen />;
    case "entities":
      return <ContactDrawerEntitiesScreen />;
    case "invoices":
      return <ContactDrawerInvoicesScreen />;
    case "credits":
      return <ContactDrawerCreditsScreen />;
    case "activity":
      return <ContactDrawerActivityScreen />;
    default:
      return <ContactDrawerDetailsScreen />;
  }
}

function DialogHeading({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  const contact = useContactDrawer();
  const t = useTranslations("contact");
  const profileImage = contact.profilePicture?.url;

  return (
    <DrawerHeader>
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          {profileImage ? (
            <img
              src={profileImage}
              alt={contact.name ?? "Contact"}
              className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : null}
          <div>
            <DrawerTitle>{contact.name ?? "--"}</DrawerTitle>
            <DrawerDescription>
              {contact.legal_name ? contact.legal_name : null}
            </DrawerDescription>
            <div className="mt-1 flex gap-2">
              <Badge color={contactTypeColorMap[contact.type]}>
                {t(`type.${contact.type}`)}
              </Badge>
            </div>
          </div>
        </div>

        <ContactDrawerActions contact={contact} />
      </div>

      <div className="px-6">
        <ContactDrawerTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </DrawerHeader>
  );
}

function ContactDrawerDetailsScreen() {
  const contact = useContactDrawer();
  const { mutateAsync } = useGqlMutation(updateContactMutation);

  return (
    <div className="space-y-8">
      <RecordScreenFormCard<UpdateContactInput>
        title="Details"
        initialValues={{
          name: contact.name,
          company_number: contact.company_number,
          legal_name: contact.legal_name,
          purchase_order_number: contact.purchase_order_number,
          billing_cycle_day_of_month: contact.billing_cycle_day_of_month,
          billing_email: contact.billing_email,
          billing_phone: contact.billing_phone,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: contact.id,
            input: values,
          });
        }}
      >
        <ContactDetailsForm contact={contact} />
      </RecordScreenFormCard>

      <RecordScreenFormCard<UpdateContactInput>
        title="Billing address"
        initialValues={{
          billingAddress: {
            upsert: {
              id: contact.billingAddress?.id,
              city: contact.billingAddress?.city ?? null,
              country: contact.billingAddress?.country ?? null,
              postcode: contact.billingAddress?.postcode ?? null,
              address_line_1: contact.billingAddress?.address_line_1 ?? null,
              address_line_2: contact.billingAddress?.address_line_2 ?? null,
              state: contact.billingAddress?.state ?? null,
            },
          },
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: contact.id,
            input: values,
          });
        }}
      >
        <AddressInput
          name="billingAddress.upsert"
          label="Address"
          className="col-span-5"
          initialLat={contact.billingAddress?.lat}
          initialLng={contact.billingAddress?.lng}
        />
      </RecordScreenFormCard>
    </div>
  );
}

function ContactDrawerEntitiesScreen() {
  const contact = useContactDrawer();
  const { open } = useDialog(ContactEntityCreateDialog);

  return (
    <div className="space-y-8">
      <ContactEntityTable
        title="Entities"
        id="contact-entities"
        rightButtons={
          <Button
            LeadingIcon={PlusIcon}
            onClick={() => {
              open({
                contactId: contact.id,
              });
            }}
          >
            Add entity
          </Button>
        }
        queryVariables={{
          type: [ContactType.Entity],
          parentContactId: contact.id,
        }}
      />
    </div>
  );
}

function ContactDrawerInvoicesScreen() {
  const contact = useContactDrawer();

  return (
    <div className="space-y-8">
      <InvoiceTable
        id="contact-invoices"
        queryVariables={{
          payeeContactId: contact.id,
        }}
      />

      <PaymentTable
        title="Upcoming payments"
        hiddenColumns={["payee"]}
        id="contact-upcoming-payments"
        queryVariables={{
          status: [PaymentStatus.Unpaid],
          payeeContactId: contact.id,
        }}
      />
    </div>
  );
}

function ContactDrawerCreditsScreen() {
  const contact = useContactDrawer();

  return (
    <div className="space-y-8">
      <RecordScreenCard
        title="Credit balance"
        subtitle="Credit comes off the next invoice raised in the same currency"
        buttons={<IssueContactCreditButton contactId={contact.id} />}
      >
        <ContactCreditBalances contactId={contact.id} />
      </RecordScreenCard>

      <ContactCreditTable
        title="Ledger"
        id="contact-credits"
        queryVariables={{
          contactId: contact.id,
        }}
      />
    </div>
  );
}

function ContactDrawerActivityScreen() {
  const contact = useContactDrawer();
  return (
    <div>
      <ActivityLogFeed modelId={contact.id} modelType={ModelType.Contact} />
    </div>
  );
}

function ContactDrawerActions({ contact }: { contact: any }) {
  const getActions = useContactActions();

  if (!contact) {
    return null;
  }

  const actionGroups = getActions(contact);

  return <ActionGroupButtons buttons={actionGroups} />;
}
